<template>
    <div class="search-results-content">
        <SpellingCorrection v-if="mustDisplaySpellingCorrection" />
        <NoResultFound v-if="!isLoading && noResultsOrOnlyFallbackResults" />
        <div v-if="hasInitialSearchResults">
            <SearchTitleSkeleton v-if="initialSearchIsLoading" />
            <InitialSearchTitle v-else />
            <SearchResultsHint v-if="isBrowsingGlobalMatching" />
            <ResultsGridLayout class="search-profiles__cards">
                <template v-if="mustDisplayStrategyBanner" #banner>
                    <StrategyLowLevelBanner />
                </template>
                <ProfileCardSearchResult
                    v-for="(profile, index) in initialSearchResponse?.profiles"
                    :key="profile.id"
                    :profile="profile"
                    :skeleton="initialSearchIsLoading"
                    :data-testid="`profile-card-initial-search-${index}`"
                    :index="index"
                />
            </ResultsGridLayout>
        </div>
        <div v-if="hasRemoteFallbackResults">
            <SearchTitleSkeleton v-if="remoteFallbackIsLoading" />
            <RemoteFallbackSearchTitle v-else />
            <ResultsGridLayout class="search-profiles__cards">
                <template v-if="mustDisplayStrategyBannerInRemoteFallbackResults" #banner>
                    <StrategyLowLevelBanner />
                </template>
                <ProfileCardSearchResult
                    v-for="(profile, index) in remoteFallbackSearchResponse?.profiles"
                    :key="profile.id"
                    :profile="profile"
                    :skeleton="remoteFallbackIsLoading"
                    :data-testid="`profile-card-remote-fallback-${index}`"
                    :index="index"
                />
            </ResultsGridLayout>
        </div>
        <div v-if="hasEuropaFallbackResults">
            <SearchTitleSkeleton v-if="europaFallbackIsLoading" />
            <div v-else>
                <EuropaFallbackSearchTitle />
                <EuropaFallbackTranslation />
            </div>
            <ResultsGridLayout class="search-profiles__cards">
                <template v-if="mustDisplayStrategyBannerInEuropaFallbackResults" #banner>
                    <StrategyLowLevelBanner />
                </template>
                <ProfileCardSearchResult
                    v-for="(profile, index) in europaFallbackSearchResponse?.profiles"
                    :key="profile.id"
                    :profile="profile"
                    :skeleton="europaFallbackIsLoading"
                    :data-testid="`profile-card-europa-fallback-${index}`"
                    :index="index"
                />
            </ResultsGridLayout>
        </div>
        <LoadingProfileCardsRow v-if="noPreviousResultsLoaderDisplayed" />
    </div>
    <SearchResultsFooter />
    <BrowseProfilesProductTour v-if="currentIdentityIsClient" />
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useAuth} from '#imports';
    import EuropaFallbackTranslation from './EuropaFallbackTranslation.vue';
    import {useProfilesStore} from '../stores/profiles.store';
    import {useSearchFeaturesStore} from '../stores/search-features.store';
    import BrowseProfilesProductTour from './client-onboarding/BrowseProfilesProductTour.client.vue';
    import StrategyLowLevelBanner from './malt-strategy-banners/StrategyLowLevelBanner.vue';
    import NoResultFound from './NoResultFound.vue';
    import LoadingProfileCardsRow from './profile-cards/LoadingProfileCardsRow.vue';
    import ProfileCardSearchResult from './profile-cards/ProfileCardSearchResult.vue';
    import EuropaFallbackSearchTitle from './search-titles/EuropaFallbackSearchTitle.vue';
    import InitialSearchTitle from './search-titles/InitialSearchTitle.vue';
    import RemoteFallbackSearchTitle from './search-titles/RemoteFallbackSearchTitle.vue';
    import SearchResultsHint from './search-titles/SearchResultsHint.vue';
    import SearchTitleSkeleton from './search-titles/SearchTitleSkeleton.vue';
    import SearchResultsFooter from './SearchResultsFooter.vue';
    import SpellingCorrection from './SpellingCorrection.vue';
    import ResultsGridLayout from '../layouts/ResultsGridLayout.vue';

    const PROFILES_COUNT_TO_HIDE_SPELLING_CORRECTION = 24;

    const authStore = useAuth();
    const profilesStore = useProfilesStore();
    const searchFeaturesStore = useSearchFeaturesStore();
    const {
        isLoading,
        initialSearchIsLoading,
        initialSearchResponse,
        remoteFallbackIsLoading,
        remoteFallbackSearchResponse,
        europaFallbackIsLoading,
        europaFallbackSearchResponse,
        totalResultCount,
    } = storeToRefs(profilesStore);
    const {isPotentiallyStrategyEligible, isAbsolutelyStrategyEligible, spellingCorrection} = storeToRefs(searchFeaturesStore);
    const {currentIdentityIsClient} = storeToRefs(authStore);

    const hasInitialSearchResults = computed(() => (initialSearchResponse.value?.pagination.totalElements ?? 0) > 0);
    const hasRemoteFallbackResults = computed(() => (remoteFallbackSearchResponse.value?.pagination.totalElements ?? 0) > 0);
    const hasEuropaFallbackResults = computed(() => (europaFallbackSearchResponse.value?.pagination.totalElements ?? 0) > 0);

    const mustDisplayStrategyBanner = computed(() => isPotentiallyStrategyEligible.value || isAbsolutelyStrategyEligible.value);
    const mustDisplayStrategyBannerInRemoteFallbackResults = computed(() => mustDisplayStrategyBanner.value && !hasInitialSearchResults.value);
    const mustDisplayStrategyBannerInEuropaFallbackResults = computed(
        () => mustDisplayStrategyBanner.value && !hasInitialSearchResults.value && !hasRemoteFallbackResults.value,
    );
    const mustDisplaySpellingCorrection = computed(
        () =>
            !!spellingCorrection.value &&
            hasInitialSearchResults.value &&
            (initialSearchResponse.value?.pagination.totalElements ?? 0) < PROFILES_COUNT_TO_HIDE_SPELLING_CORRECTION,
    );

    const noPreviousResultsLoaderDisplayed = computed(() => {
        const previousSearchHadNoResultsAndIsLoading = totalResultCount.value === 0 && isLoading.value;
        const prevInitialSearchHadNoResultsAndIsLoading = !hasInitialSearchResults.value && initialSearchIsLoading.value;
        const initialSearchIsLoadedAndPrevRemoteFallbackHadNoResultsAndIsLoading =
            !initialSearchIsLoading.value && !hasRemoteFallbackResults.value && remoteFallbackIsLoading.value;
        const remoteFallbackIsLoadedAndPrevEuropaFallbackHadNoResultsAndIsLoading =
            !remoteFallbackIsLoading.value && !hasEuropaFallbackResults.value && europaFallbackIsLoading.value;
        return (
            previousSearchHadNoResultsAndIsLoading ||
            prevInitialSearchHadNoResultsAndIsLoading ||
            initialSearchIsLoadedAndPrevRemoteFallbackHadNoResultsAndIsLoading ||
            remoteFallbackIsLoadedAndPrevEuropaFallbackHadNoResultsAndIsLoading
        );
    });

    const noResultsOrOnlyFallbackResults = computed(() => totalResultCount.value === 0 || !hasInitialSearchResults.value);

    const isBrowsingGlobalMatching = computed(
        () => initialSearchResponse.value?.searchType == 'GLOBAL_DOMAIN' || initialSearchResponse.value?.searchType == 'UNIFIED_KEYWORD_SEARCH',
    );
</script>

<style lang="scss">
    .search-results-content {
        display: grid;
        row-gap: var(--joy-core-spacing-12);
    }

    .search-profiles {
        &__cards {
            margin-top: var(--joy-core-spacing-6);
        }
    }
</style>
